<template>
  <div class="row">
    <div class="col-md-12 col-xxl-12 col-sm-12 mb-10">
      <div class="card mb-25 border-0 rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">

          <!-- En-tête -->
          <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 mb-4">
            <router-link to="/liste-compositions" class="btn btn-primary transition border-0 lh-1 fw-medium">
              <i class="flaticon-left-arrow lh-1 me-1 position-relative top-2"></i>
              <span class="position-relative">Retour</span>
            </router-link>
            <h4 class="mb-0 mt-3 mt-lg-0">Détails de la composition</h4>
          </div>

          <div class="composition-details">

            <!-- Informations générales -->
            <div class="info-section mb-4">
              <h5 class="section-title fw-bold text-dark">
                <i class="fas fa-info-circle me-2"></i>Informations Générales
              </h5>
              <div class="info-grid">
                <div v-if="composition?.titre" class="info-item">
                  <label>Titre</label>
                  <p class="value">{{ composition?.titre }}</p>
                </div>

                <div v-if="composition?.dateComposition" class="info-item">
                  <label>Date de composition</label>
                  <p class="value">{{ format_date_only(composition?.dateComposition) }}</p>
                </div>

                <!-- Date limite -->
                <div v-if="composition?.dateFinSoumission" class="info-item deadline-card">
                  <label class="text-danger fw-semibold">
                    <i class="fas fa-calendar-times me-2"></i>Date limite de soumission
                  </label>
                  <p class="value text-danger fw-bold fs-5 mb-0">
                    {{ format_date_only(composition?.dateFinSoumission) }}
                  </p>
                  <small class="text-muted d-block mt-1">
                    Aucune soumission acceptée après cette date
                  </small>
                </div>

                <!-- Type de composition -->
                <div v-if="composition?.type || composition?.typeComposition" class="info-item">
                  <label>Type de composition</label>
                  <p class="value">
                    <span class="badge-custom">
                      {{ (composition?.type || composition?.typeComposition) === 'QCM' ? 'QCM' : 'Questions/Réponses' }}
                    </span>
                  </p>
                </div>

                <!-- Année académique : même style que le type -->
                <div v-if="composition?.annee" class="info-item">
                  <label>Année Académique</label>
                  <p class="value">
                    <span class="badge-custom">
                      <i class="fas fa-calendar-alt me-1"></i>
                      {{ composition?.annee.libelle }}
                    </span>
                    <br>
                    <small class="text-muted">
                      Du {{ format_date_only(composition?.annee.dateDebut) }} 
                      au {{ format_date_only(composition?.annee.dateFin) }}
                    </small>
                  </p>
                </div>
              </div>
            </div>

            <!-- Informations académiques : une seule ligne -->
            <div class="info-section mb-4">
              <h5 class="section-title fw-bold text-dark">
                <i class="fas fa-graduation-cap me-2"></i>Informations Académiques
              </h5>
              <div class="info-grid-horizontal">
                <div v-if="composition?.filiereNiveauMatiere?.filiere" class="info-item">
                  <label>Filière</label>
                  <p class="value">{{ composition?.filiereNiveauMatiere.filiere.libelle }}</p>
                </div>

                <div v-if="composition?.filiereNiveauMatiere?.niveau" class="info-item">
                  <label>Niveau</label>
                  <p class="value">{{ composition?.filiereNiveauMatiere.niveau.libelle }}</p>
                </div>

                <div v-if="composition?.filiereNiveauMatiere?.matiere" class="info-item">
                  <label>Matière</label>
                  <p class="value">
                    {{ composition?.filiereNiveauMatiere.matiere.libelle }}
                    <span v-if="composition?.filiereNiveauMatiere.coefficient" class="badge bg-info text-white ms-2">
                      Coef. {{ composition?.filiereNiveauMatiere.coefficient }}
                    </span>
                  </p>
                </div>

                <div v-if="composition?.professeur" class="info-item">
                  <label>Professeur</label>
                  <p class="value">
                    <strong>{{ `${composition?.professeur.nom} ${composition?.professeur.prenom}` }}</strong>
                    <br v-if="composition?.professeur.email || composition?.professeur.npi">
                    <small class="text-muted d-block" v-if="composition?.professeur.email">
                      <i class="fas fa-envelope me-1"></i>{{ composition?.professeur.email }}
                    </small>
                    <small class="text-muted d-block" v-if="composition?.professeur.npi">
                      <i class="fas fa-phone me-1"></i>{{ composition?.professeur.npi }}
                    </small>
                  </p>
                </div>
              </div>
            </div>

            <!-- Questions -->
            <div v-if="composition?.compoQuestions && composition?.compoQuestions.length > 0" class="info-section">
              <h5 class="section-title fw-bold text-dark">
                <i class="fas fa-question-circle me-2"></i>Questions 
                <span class="badge bg-primary text-white ms-2">{{ composition?.compoQuestions.length }}</span>
              </h5>
              
              <!-- Message si date pas expirée -->
              <div v-if="!dateExpire" class="alert alert-warning mb-4 d-flex align-items-center">
                <i class="fas fa-info-circle me-2 fs-4"></i>
                <div>
                  <strong>Réponses masquées</strong><br>
                  <small>Les bonnes réponses seront visibles après la date limite de soumission : 
                    <strong>{{ format_date_only(composition?.dateFinSoumission) }}</strong>
                  </small>
                </div>
              </div>
              
              <div class="questions-list">
                <div v-for="(compoQuestion, index) in composition?.compoQuestions" :key="index" class="question-card">
                  <div class="question-header">
                    <div class="question-number">
                      <span class="number-badge">{{ index + 1 }}</span>
                    </div>
                    <div class="question-content-wrapper flex-grow-1">
                      <p class="question-text">{{ compoQuestion.question?.contenu }}</p>
                      
                      <div v-if="compoQuestion.question?.propositions && compoQuestion.question.propositions.length > 0" class="propositions-list">
                        <div class="propositions-header">
                          <i class="fas fa-list-ul me-2"></i>Choix de réponses
                        </div>
                        <div class="propositions-items">
                          <div v-for="(proposition, propIndex) in compoQuestion.question.propositions" 
                               :key="propIndex" 
                               class="proposition-item"
                               :class="{ 'correct-answer': dateExpire && proposition.estLaBonneReponse }">
                            <div class="proposition-letter">
                              {{ String.fromCharCode(65 + propIndex) }}
                            </div>
                            <div class="proposition-content">
                              {{ proposition.contenu }}
                            </div>
                            <div v-if="dateExpire && proposition.estLaBonneReponse" class="correct-indicator">
                              <i class="fas fa-check-circle"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-else-if="dateExpire && compoQuestion.question?.reponse" class="reponse-attendue">
                        <div class="reponse-header">
                          <i class="fas fa-lightbulb me-2"></i>Réponse attendue
                        </div>
                        <div class="reponse-content">
                          {{ compoQuestion.question.reponse }}
                        </div>
                      </div>
                      
                      <!-- Message si date pas expirée -->
                      <div v-else-if="!dateExpire && compoQuestion.question?.reponse" class="reponse-masquee">
                        <div class="reponse-header text-muted">
                          <i class="fas fa-lock me-2"></i>Réponse attendue
                        </div>
                        <div class="reponse-content-masque">
                          <i class="fas fa-eye-slash me-2"></i>
                          Les réponses seront visibles après la date limite de soumission
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { error, format_date } from "@/utils/utils";
import { useRoute, useRouter } from "vue-router";
import { Composition } from "@/models/Composition";
import Swal from "sweetalert2";
import ApiService from "@/services/ApiService";

export default defineComponent({
  name: "ViewComposition",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const composition = ref<Composition | null>(null);
    const dateExpire = ref<boolean>(false);

    const format_date_only = (dateString: string) => {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric"
      });
    };


    function getComposition(id: string) {
      return ApiService.get(`/compositions/${id}`)
        .then(({ data }) => {
           console.log("infffffffffffffoooo",data);
          composition.value = data.data;
          dateExpire.value = data.data?.dateExpire || false;
        })
        .catch(({ response }) => {

         
          if (response?.status === 403 && response?.data?.error?.includes('Date non expirée')) {
            Swal.fire({
              icon: 'warning',
              title: 'Composition non disponible',
              html: `<p>${response.data.error}</p>
                     <p class="text-muted mt-2"><small>Vous pourrez voir les réponses après cette date.</small></p>`,
              confirmButtonText: 'Compris',
              confirmButtonColor: '#007bff',
            }).then(() => {
              router.push('/liste-compositions');
            });
          } else {
            error(response?.data?.message || response?.data?.error || "Erreur lors de la récupération");
          }
        });
    }

    onMounted(() => {
      const id = route.params.id as string;
      getComposition(id);
    });

    return {
      composition,
      dateExpire,
      format_date,
      format_date_only,
    };
  },
});
</script>

<style scoped>
.composition-details {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
}

.info-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #007bff;
  margin-bottom: 24px;
}

/* Tous les titres en gras + noir pur */
.section-title {
  color: #000 !important;
  font-size: 1.15rem;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.info-grid-horizontal {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 10px;
}

.info-grid-horizontal .info-item {
  min-width: 250px;
  flex: 1;
}

.info-item {
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.info-item:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.info-item label {
  display: block;
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.info-item .value {
  color: #2c3e50;
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
  font-weight: normal;
}

/* Date limite */
.deadline-card {
  border-left: 4px solid #dc3545 !important;
  background: #fff5f5;
}

/* Badges identiques : Type + Année académique */
.badge-custom {
  display: inline-flex;
  align-items: center;
  background: #007bff;
  color: white;
  padding: 8px 16px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: none;
}

/* Questions */
.questions-list { display: flex; flex-direction: column; gap: 20px; }
.question-card { background: white; border-radius: 8px; border: 2px solid #e9ecef; overflow: hidden; }
.question-card:hover { border-color: #007bff; box-shadow: 0 6px 20px rgba(0,123,255,0.15); }
.question-header { display: flex; gap: 15px; padding: 20px; }
.number-badge { 
  display: flex; align-items: center; justify-content: center; 
  width: 40px; height: 40px; background: #007bff; color: white; 
  border-radius: 50%; font-weight: 700; font-size: 1.1rem; flex-shrink: 0; 
}
.question-text { font-weight: 600; color: #2c3e50; margin-bottom: 15px; }

.proposition-item { display: flex; align-items: center; gap: 12px; padding: 12px 15px; background: #f8f9fa; border-radius: 6px; }
.proposition-item.correct-answer { background: #d4edda; border: 2px solid #28a745; }
.proposition-letter { 
  width: 32px; height: 32px; background: white; border: 2px solid #dee2e6; 
  border-radius: 50%; display: flex; align-items: center; justify-content: center; 
  font-weight: 700; flex-shrink: 0; 
}
.correct-answer .proposition-letter { background: #28a745; color: white; border-color: #28a745; }
.reponse-content { background: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; border-radius: 6px; font-style: italic; }

/* Styles pour réponses masquées */
.reponse-masquee { margin-top: 15px; }
.reponse-content-masque { 
  background: #f8f9fa; 
  border-left: 4px solid #6c757d; 
  padding: 15px; 
  border-radius: 6px; 
  font-style: italic;
  color: #6c757d;
}

@media (max-width: 992px) {
  .info-grid-horizontal { flex-wrap: wrap; }
  .info-grid-horizontal .info-item { min-width: 45%; }
}

@media (max-width: 768px) {
  .info-grid { grid-template-columns: 1fr; }
  .info-grid-horizontal .info-item { min-width: 100%; }
}
</style>